/**
 * This file contains the JsonLd component for creating structured data markup
 * which is used for SEO.
 * You can use this component with the exported schema functions like this:
 * <JsonLd schema={videoObjectSchema({...})} />
 * The various schema functions will add the necessary "@type" properties.
 *
 * The possible props for each schema is quite vast, so we only list what we
 * use in our Props type defs, but if you need to expand it you can look at
 * the schema.org url for what properties are allowed for that schema.
 *
 * Naming:
 * <SchemaType>Props is the type of data passed into a schema function
 * <SchemaType>Schema is the schema.org-compatible type
 * each schema function is the Schema type with a lower-case first character
 */

import * as React from "react";

import type {
    KeyMoment,
    PublishedStandard,
} from "#content-library/types/types.ts";
import {escapeEntities} from "#rendering/escape-entities.ts";

import {setIdToFramework} from "./data-utils.ts";

type Schema = {
    ["@type"]: string | Array<string>;
};

/**
 * @see https://schema.org/Answer
 */
type AnswerSchema = Schema & {
    ["@type"]: "Answer";
} & AnswerProps;
type AnswerProps = {
    text: string;
};
// Structured data for https://schema.org/Answer
export function answerSchema(props: AnswerProps): AnswerSchema {
    return {
        "@type": "Answer",
        text: props.text,
    };
}

/**
 * @see https://schema.org/FAQPage
 */
type FAQPageSchema = Schema & {
    ["@type"]: "FAQPage";
    mainEntity: Array<QuestionSchema>;
};
type FAQPageProps = {
    mainEntity: Array<QuestionProps>;
};
// Structured data for https://schema.org/FAQPage
export function faqPageSchema({mainEntity}: FAQPageProps): FAQPageSchema {
    const schemaProps = {
        mainEntity: mainEntity.map(({name, acceptedAnswer}) =>
            questionSchema({name, acceptedAnswer}),
        ),
    } as const;
    return {
        "@type": "FAQPage",
        ...schemaProps,
    };
}

/**
 * @see https://schema.org/Question
 */
type QuestionSchema = Schema & {
    ["@type"]: "Question";
    name: string;
    acceptedAnswer: AnswerSchema;
};
type QuestionProps = {
    name: string;
    acceptedAnswer: AnswerProps;
};
// Structured data for https://schema.org/Question
export function questionSchema(props: QuestionProps): QuestionSchema {
    return {
        "@type": "Question",
        name: props.name,
        acceptedAnswer: answerSchema(props.acceptedAnswer),
    };
}

type AlignmentObjectFields = {
    // developers.google.com/search/docs/appearance/structured-data/learning-video
    targetName?: string | null | undefined;
    targetUrl?: string | null | undefined;
    educationalFramework?: string | null | undefined;
    // more fields from https://schema.org/AlignmentObject
    alignmentType?: string | null | undefined;
    targetDescription?: string | null | undefined;
};
/**
 * @see https://schema.org/AlignmentObject
 */
type AlignmentObjectSchema = Schema & {
    ["@type"]: "AlignmentObject";
} & AlignmentObjectFields;

/**
 * EducationalLevel and LearningResourceType are required to be one of a few
 * values.  These enums are an extra layer to enforce that.
 * @see https://developers.google.com/search/docs/appearance/structured-data/learning-video#learning-video-[videoobject,-learningresource]
 */
enum EducationalLevel {
    Beginner = "Beginner",
    Intermediate = "Intermediate",
    Advanced = "Advanced",
}
enum LearningResourceType {
    ConceptOverview = "Concept overview",
    HowTo = "How-to",
    Tips = "Tips",
    ProblemWalkthrough = "Problem walkthrough",
}

type LearningResourceFields = {
    // developers.google.com/search/docs/appearance/structured-data/learning-video
    educationalAlignment?: AlignmentObjectSchema | Array<AlignmentObjectSchema>;
    educationalLevel?: EducationalLevel | null | undefined;
    learningResourceType?: LearningResourceType | null | undefined;
    // and https://schema.org/LearningResource
    assesses?: string | null | undefined;
    competencyRequired?: string | null | undefined;
    educationalUse?: string | null | undefined;
    teaches?: string | null | undefined;
};
/**
 * @see https://schema.org/LearningResource
 * @property {"LearningResource"} "@type"
 */
type LearningResourceSchema = Schema & {
    ["@type"]: "LearningResource";
} & LearningResourceFields;

/**
 * @see https://schema.org/VideoObject
 */
export type HasPartSchema = {
    ["@type"]: "Clip";
    name: string;
    startOffset: number;
    endOffset: number;
    url: string;
};

type VideoObjectSchema = Schema & {
    ["@type"]: "VideoObject";
    name?: string | null | undefined;
    description?: string | null | undefined;
    uploadDate?: string | null | undefined;
    thumbnailUrl?: string | null | undefined;
    transcript?: string | null | undefined;
    contentUrl?: string | null | undefined;
    embedUrl?: string | null | undefined; // TODO(mp-3760): Remove temporary property
    url?: string | null | undefined;
    potentialAction?: {
        ["@type"]: "SeekToAction";
        target: string;
        ["startOffset-input"]: "required name=seek_to_second_number";
    };
    hasPart?: Array<HasPartSchema>;
};
type VideoObjectProps = {
    name?: string | null | undefined;
    description?: string | null | undefined;
    uploadDate?: string | null | undefined;
    thumbnailUrl?: string | null | undefined;
    transcript?: string | null | undefined;
    contentUrl?: string | null | undefined;
    potentialActionTargetUrl?: string | null | undefined;
    url?: string | null | undefined;
    keyMoments?: ReadonlyArray<KeyMoment> | null | undefined;
};

/**
 * @see https://developers.google.com/search/docs/appearance/structured-data/learning-video
 */
type LearningVideoSchema = Schema &
    VideoObjectSchema &
    LearningResourceSchema & {
        ["@type"]: ["VideoObject", "LearningResource"];
    };

// Structured data for https://schema.org/VideoObject
export function videoObjectSchema(props: VideoObjectProps): VideoObjectSchema {
    const {potentialActionTargetUrl, contentUrl, keyMoments, ...rest} = props;
    const schema: VideoObjectSchema = {
        "@type": "VideoObject",
        ...rest,
    };

    // The video url is required for clip structured data, so return schema as
    // is if that is missing
    if (potentialActionTargetUrl == null) {
        return schema;
    }

    // Use our key moments if they are available, otherwise provide SeekToAction
    // markup with hope that Google generates the key moments for us
    if (keyMoments != null && keyMoments.length > 0) {
        schema.hasPart = _mapKeyMomentsToSchema(
            keyMoments,
            potentialActionTargetUrl,
        );
    } else {
        schema.contentUrl = contentUrl;
        schema.potentialAction = {
            "@type": "SeekToAction",
            target: `${potentialActionTargetUrl}?t={seek_to_second_number}`,
            // This exact value for startOffset-input is required
            // See https://developers.google.com/search/docs/appearance/structured-data/video#seek
            "startOffset-input": "required name=seek_to_second_number",
        };
    }

    return schema;
}

// _mapKeyMomentsToSchema maps our key moments data to an array of clips
// structured data
export const _mapKeyMomentsToSchema = (
    keyMoments: ReadonlyArray<KeyMoment>,
    url: string,
): Array<HasPartSchema> =>
    keyMoments.map((keyMoment) => ({
        "@type": "Clip",
        url: `${url}?t=${keyMoment.startOffset}`,
        startOffset: keyMoment.startOffset,
        endOffset: keyMoment.endOffset,
        name: keyMoment.label,
    }));

// will map standards to alignment objects
type LearningResourceProps = {
    fields?: Partial<Omit<LearningResourceFields, "educationalAlignment">>;
    standards?: Array<PublishedStandard> | null | undefined;
    educationalLevel?: string | null | undefined;
    learningResourceType?: string | null | undefined;
};
const learningResourceSchema = (
    props?: LearningResourceProps | null,
): LearningResourceSchema => {
    const {standards, educationalLevel, learningResourceType} = props ?? {};

    return {
        "@type": "LearningResource",
        ...props?.fields,
        // default to "Intermediate" if educational level is missing or
        // incorrect
        // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type '"Intermediate" | "Beginner" | "Advanced" | null | undefined'. | TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type '"Intermediate" | "Beginner" | "Advanced"'.
        educationalLevel: Object.values(EducationalLevel).includes(
            // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'EducationalLevel'.
            educationalLevel,
        )
            ? educationalLevel
            : EducationalLevel.Intermediate,
        // default to "Concept overview" if learning resource type is missing or
        // incorrect
        // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type '"Concept overview" | "How-to" | "Tips" | "Problem walkthrough" | null | undefined'.
        learningResourceType: Object.values(LearningResourceType).includes(
            // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type '"Concept overview" | "How-to" | "Tips" | "Problem walkthrough"'.
            learningResourceType,
        )
            ? learningResourceType
            : LearningResourceType.ConceptOverview,
        educationalAlignment: standards?.map((standard) => {
            const alignment: AlignmentObjectSchema = {
                "@type": "AlignmentObject",
            };
            if (standard?.setId && standard?.standardId) {
                alignment.targetName = `${standard.setId}.CONTENT.${standard.standardId}`;
                alignment.educationalFramework = setIdToFramework(
                    standard.setId,
                );
                alignment.targetDescription = standard.description;
            }
            return alignment;
        }),
    };
};

export function learningVideoSchema(
    videoObjectProps: VideoObjectProps,
    learningResourceProps?: LearningResourceProps | null,
): LearningVideoSchema {
    return {
        ...videoObjectSchema(videoObjectProps),
        ...learningResourceSchema(learningResourceProps),
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'. | TS2322 - Type 'string' is not assignable to type 'never'.
        "@type": ["VideoObject", "LearningResource"],
    };
}

// sanitizeJsonLdSchema recursively traverses the schema, removing null
// properties and escaping any illegal characters in fields
export function sanitizeJsonLdSchema(schema: AnySchema) {
    Object.keys(schema).forEach((key) => {
        // remove null properties
        // Not all AnySchema types have the same props
        if (schema[key] == null) {
            // Not all AnySchema types have the same props
            delete schema[key];
        }
        // recursively sanitize child objects
        else if (typeof schema[key] === "object") {
            // Not all AnySchema types have the same props
            sanitizeJsonLdSchema(schema[key]);
        }
        // numbers can never be corrupted (nothing to do for numbers)
        else if (typeof schema[key] === "number") {
            return;
        }
        // escape entities for strings
        else {
            // Not all AnySchema types have the same props
            schema[key] = escapeEntities(schema[key]);
        }
    });
}

type AnySchema =
    | VideoObjectSchema
    | FAQPageSchema
    | QuestionSchema
    | AnswerSchema
    | LearningResourceSchema
    | LearningVideoSchema
    | AlignmentObjectSchema;

type Props = {
    schema: AnySchema;
};

// JsonLd is a simple component which creates the markup for JSON-LD
// structured data.
export function JsonLd(props: Props): React.ReactElement {
    sanitizeJsonLdSchema(props.schema);

    const schemaWithContext = {
        "@context": "https://schema.org",
        ...props.schema,
    } as const;

    return (
        <script
            type="application/ld+json"
            data-testid="jsonld"
            dangerouslySetInnerHTML={{
                __html: JSON.stringify(schemaWithContext),
            }}
        />
    );
}
